import { AddCard, CreditCard } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Collapse, Container, Fade, ToggleButton, ToggleButtonGroup } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import PaymentButton from '@acorn-portal/shared/src/components/buttons/PaymentButton';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import ProblemModal from '@acorn-portal/shared/src/components/modal/ProblemModal';
import DebitCardSelector, {
	IExistingCardPaymentFormValues,
	EXISTING_CARD_FORM_INITIAL_VALUES,
	existingCardFormValidationSchema,
} from '@acorn-portal/shared/src/components/payment/DebitCardSelector';
import { Form, Formik } from 'formik';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import {
	handleGetClientData,
	handleMakePaymentIntent,
	handleTakeUpMidTermAdjustment,
} from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
	Address,
	MtaPaymentCollectionOption,
	PaymentMethods,
	PaymentProvider,
} from '@acorn-portal/shared/src/services/api';
import { resolvePreparePaymentSource } from '@acorn-portal/shared/src/utils/payment/resolve-payment-source';
import { PAYMENT_PROBLEM_MODAL } from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import BraintreePaymentForm, {
	BRAINTREE_FORM_INITIAL_VALUES,
	IBraintreeCardPaymentFormValues,
	braintreeCardFormValidationSchema,
} from '@acorn-portal/shared/src/components/forms/braintree-payment-form';
import DirectPaymentVendors from '@acorn-portal/shared/src/components/vendor/DirectPaymentVendors';
import { useBraintreeContext } from '@acorn-portal/shared/src/context/braintree-context';
import { ExtendPaymentMethodOptions } from '@acorn-portal/shared/src/utils/types/extensions/braintree';
import { hasErrorCode, handleErrorCodeExceptions } from '../../../utils/handle-error-codes-exceptions';
import { useAuth } from 'react-oidc-context';

interface IProps {
	payInFullOption: MtaPaymentCollectionOption;
	toggleModal: (open: boolean) => void;
}

const handleCardHolderAddress = (address: Address): Address => {
	const line1Check = address.line1;
	if (line1Check.length > 50) {
		const line1 = line1Check.split(',')[0];
		const line2 = line1Check.split(',')[1].startsWith(' ')
			? line1Check.split(',')[1].substring(1)
			: line1Check.split(',')[1];
		const line3 = address.line2;

		return {
			...address,
			line1,
			line2,
			line3,
		};
	}

	return address;
};

const BraintreeCardPaymentSection = ({ payInFullOption, toggleModal }: IProps) => {
	const auth = useAuth();
	const midTermAdjustmentResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const hasExistingCards = false;
	const [useExistingCard, setUseExistingCard] = useState(hasExistingCards);
	const [problem, setProblem] = useState(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const [localLoading, setLocalLoading] = useState(false);
	const { instance, isPaymentDetailsValid } = useBraintreeContext();
	const navigate = useNavigate();
	const contactPreferences = useAppSelector((redux) => redux.contactPreferences);
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const policyHolder = midTermAdjustment.drivers.find((x) => x.relationshipToProposer === 'P');

	const handleBraintreePaymentProblem = async () => {
		setProblem(true);
		instance?.clearSelectedPaymentMethod();
	};

	const onSubmitBraintreeForm = async (values: IBraintreeCardPaymentFormValues) => {
		setError(undefined);
		setLocalLoading(true);
		const { saveCard, cardholderAddress } = values;

		if (!instance || !payInFullOption.totalAmountPayable) {
			setProblem(true);
			return;
		}

		try {
			// The payment source (card vs wallet) is only known from the tokenised payment method, and
			// that determines PreparePaymentSource on the payment intent - so tokenise first. Because the
			// intent runs after tokenisation, the 3DS ip address is fetched independently here.
			const clientData = await handleGetClientData();

			const fixedAddress = handleCardHolderAddress(values.cardholderAddress);

			const requestPayload: ExtendPaymentMethodOptions = {
				threeDSecure: {
					amount: payInFullOption.totalAmountPayable.toString(),
					challengeRequested: values.saveCard,
					email: values.cardholderEmail,
					billingAddress: {
						givenName: values.cardholderFirstName,
						surname: values.cardholderLastName,
						streetAddress: fixedAddress.line1,
						extendedAddress: fixedAddress.line2 ?? undefined,
						line3: fixedAddress.line3 ?? undefined,
						locality: fixedAddress.city ?? undefined,
						postalCode: fixedAddress.postcode,
					},
					additionalInformation: {
						ipAddress: clientData.clientIp,
					},
				},
			};

			const braintreeResponse = await instance.requestPaymentMethod(requestPayload);

			if (!braintreeResponse) {
				return handleBraintreePaymentProblem();
			}

			if (!braintreeResponse.deviceData) {
				return handleBraintreePaymentProblem();
			}

			if (
				braintreeResponse.type === 'CreditCard' &&
				(!braintreeResponse.threeDSecureInfo?.liabilityShifted ||
					!braintreeResponse.threeDSecureInfo?.liabilityShiftPossible)
			) {
				return handleBraintreePaymentProblem();
			}

			const intentResponse = await handleMakePaymentIntent(
				PaymentProvider.BRAINTREE,
				midTermAdjustment,
				{
					paymentDetails: {
						saveCard,
						amount: payInFullOption.totalAmountPayable,
						billingAddress: cardholderAddress,
					},
				},
				resolvePreparePaymentSource(braintreeResponse.type),
			);

			if (!intentResponse) throw Error('Unable to make payment intent');

			await handleTakeUpMidTermAdjustment(
				{
					policyReference: midTermAdjustment.policyReference,
					sendPostalDocuments: contactPreferences.printToPost,
					paymentId: intentResponse.paymentId,
					transactionId: midTermAdjustment.transactionId,
					takeUpDetails: {
						underwritingResponseId: midTermAdjustmentResponse.underwritingOutcomeResponseId,
						underwritingOptionId: midTermAdjustmentResponse.optionId,
						paymentCollectionOptionCode: payInFullOption.code,
						selectedBalanceSettlementMethod: PaymentMethods.CARD_PAYMENT,
					},
				},
				braintreeResponse.nonce,
				braintreeResponse.deviceData,
				intentResponse.idempotencyKey,
			);
			navigate(ROUTE_PATHS.makeAChangePaymentSuccess);
		} catch (error) {
			if (hasErrorCode(error)) {
				const paymentError = handleErrorCodeExceptions(error.response, navigate);
				if (paymentError) {
					setError(paymentError.message);
					instance?.clearSelectedPaymentMethod();
				}
			} else {
				setProblem(true);
			}
		} finally {
			setLocalLoading(false);
		}
	};

	const submitExistingCardPaymentForm = async (values: IExistingCardPaymentFormValues) => {
		setError(undefined);
		try {
			setLocalLoading(true);
			await handleTakeUpMidTermAdjustment({
				policyReference: midTermAdjustment.policyReference,
				sendPostalDocuments: contactPreferences.printToPost,
				existingCardPaymentMethodId: values.id,
				transactionId: midTermAdjustment.transactionId,
				takeUpDetails: {
					underwritingResponseId: midTermAdjustmentResponse.underwritingOutcomeResponseId,
					underwritingOptionId: midTermAdjustmentResponse.optionId,
					paymentCollectionOptionCode: payInFullOption.code,
					selectedBalanceSettlementMethod: PaymentMethods.CARD_PAYMENT,
				},
			});
			navigate(ROUTE_PATHS.makeAChangePaymentSuccess);
		} catch (error) {
			if (hasErrorCode(error)) {
				const paymentError = handleErrorCodeExceptions(error.response, navigate);
				if (paymentError) {
					setError(paymentError.message);
					instance?.clearSelectedPaymentMethod();
				}
			} else {
				setProblem(true);
			}
		} finally {
			setLocalLoading(false);
		}
	};

	if (policyHolder === undefined) return null;

	return (
		<Fade in>
			<Box>
				<LoadingModal open={localLoading} message='Processing Payment' />
				<Box sx={{ width: '100%' }}>
					<Box sx={{ margin: 2 }} />
					<Box sx={{ margin: 'auto', width: '100%', textAlign: 'center' }}>
						{hasExistingCards && (
							<ToggleButtonGroup color='primary' value={useExistingCard} exclusive aria-label='Select Card'>
								<ToggleButton
									value={false}
									onClick={() => setUseExistingCard(false)}
									sx={{ '& svg': { marginRight: '5px' } }}
									disabled={!hasExistingCards}
								>
									<AddCard />
									New Card
								</ToggleButton>
								{hasExistingCards && (
									<ToggleButton value onClick={() => setUseExistingCard(true)} sx={{ '& svg': { marginRight: '5px' } }}>
										<CreditCard />
										Existing Card
									</ToggleButton>
								)}
							</ToggleButtonGroup>
						)}
					</Box>
					<Box sx={{ margin: 3 }} />
					<Collapse
						in={!useExistingCard}
						sx={{
							'* > svg': {
								visibility: 'visible !important',
							},
						}}
					>
						<Box>
							<Formik<IBraintreeCardPaymentFormValues>
								initialValues={BRAINTREE_FORM_INITIAL_VALUES}
								onSubmit={onSubmitBraintreeForm}
								validationSchema={braintreeCardFormValidationSchema}
							>
								{() => {
									return (
										<Form>
											<Box
												sx={{
													textAlign: 'left',
													margin: 'auto',
												}}
											>
												<BraintreePaymentForm
													policyHolder={{
														address: midTermAdjustment.policyDetails.policyholder.address,
														firstName: policyHolder.firstName,
														lastName: policyHolder.lastNames,
														email: auth?.user?.profile.email ?? '',
													}}
													amountToPay={payInFullOption.totalAmountPayable?.toString() ?? ''}
												/>
											</Box>
											{error && (
												<Container maxWidth='sm' sx={{ mt: 2 }}>
													<Alert severity='error'>
														<AlertTitle>Error</AlertTitle>
														{error}
													</Alert>
												</Container>
											)}
											<Box
												sx={{
													paddingTop: 2,
													maxWidth: '400px',
													textAlign: 'center',
													margin: 'auto',
												}}
											>
												<DirectPaymentVendors />
											</Box>
											<Box sx={{ paddingTop: 2 }}>
												<ActionButtons>
													<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
													<PaymentButton type='submit' disabled={!isPaymentDetailsValid} icon='Card'>
														Make Payment
													</PaymentButton>
												</ActionButtons>
											</Box>
										</Form>
									);
								}}
							</Formik>
						</Box>
					</Collapse>
					<Collapse
						in={useExistingCard}
						sx={{
							'* > svg': {
								visibility: 'visible !important',
							},
						}}
					>
						<Formik<IExistingCardPaymentFormValues>
							initialValues={EXISTING_CARD_FORM_INITIAL_VALUES}
							onSubmit={submitExistingCardPaymentForm}
							validationSchema={existingCardFormValidationSchema}
						>
							{() => {
								return (
									<Form>
										<Box
											sx={{
												margin: 'auto',
												maxWidth: '500px',
												textAlign: 'center',
											}}
										>
											<DebitCardSelector cards={midTermAdjustmentResponse.existingCardPaymentMethods} />
										</Box>
										<Box sx={{ margin: 4 }} />
										<ActionButtons>
											<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
											<PaymentButton type='submit' icon='Card'>
												Make Payment
											</PaymentButton>
										</ActionButtons>
									</Form>
								);
							}}
						</Formik>
					</Collapse>
				</Box>
				<ProblemModal
					open={problem}
					title={PAYMENT_PROBLEM_MODAL.header}
					description={PAYMENT_PROBLEM_MODAL.text}
					onClose={() => setProblem(false)}
				/>
			</Box>
		</Fade>
	);
};

export default BraintreeCardPaymentSection;
